import { cancelledError } from "./cancelled";

// A counting semaphore whose capacity is read on every acquire rather than fixed at creation.
// Both users of it take their limit from a runtime override a contributor (or we) can change
// from the console on a real deploy, so a capacity captured once would be the capacity from
// whenever the module happened to load.
export default function createSemaphore(capacity) {
    const capacityOf = typeof capacity === "function" ? capacity : () => capacity;
    const waiting = [];
    let held = 0;

    function release() {
        held--;
        // Let through as many waiters as there is now room for, not exactly one: the capacity may
        // have been raised since they started waiting.
        while (waiting.length > 0 && held < capacityOf()) {
            held++;
            waiting.shift()();
        }
    }

    return {
        // Resolves to a releaser that is safe to call more than once, so callers can put it in a
        // `finally` without tracking whether an earlier path already released.
        //
        // A waiter whose signal aborts leaves the line without ever holding a slot, so a cancelled
        // upload waiting its turn does not keep the ones behind it waiting too.
        async acquire(signal) {
            if (signal && signal.aborted) {
                throw cancelledError();
            }
            if (held < capacityOf()) {
                held++;
            }
            else {
                await new Promise((resolve, reject) => {
                    const onAbort = () => {
                        const index = waiting.indexOf(admit);
                        if (index !== -1) {
                            waiting.splice(index, 1);
                        }
                        reject(cancelledError());
                    };
                    const admit = () => {
                        signal && signal.removeEventListener("abort", onAbort);
                        resolve();
                    };
                    waiting.push(admit);
                    signal && signal.addEventListener("abort", onAbort, { once: true });
                });
            }

            let released = false;
            return () => {
                if (!released) {
                    released = true;
                    release();
                }
            };
        },
        held: () => held,
        waiting: () => waiting.length,
    };
}
